import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { TIME_ZONE_OPTIONS, getTimeZoneLabel } from '../../utils/timezone';


const MAX_RESULTS = 120;
const VISIBLE_ITEM_HEIGHT_PX = 40;
const VISIBLE_ITEM_COUNT = 8;

function filterTimeZones(options, query) {
  const q = query.trim().toLowerCase();
  if (!q) return options;

  return options.filter(
    (zone) =>
      zone.label.toLowerCase().includes(q)
      || zone.value.toLowerCase().includes(q)
      || zone.label.replace(/[()]/g, '').toLowerCase().includes(q)
  );
}

function buildDefaultList(options, selectedValue) {
  if (!selectedValue) return options;

  const selected = options.find((zone) => zone.value === selectedValue);
  if (!selected) return options;

  return [
    selected,
    ...options.filter((zone) => zone.value !== selectedValue),
  ];
}

export default function TimeZoneDropdown({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select time zone',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [panelStyle, setPanelStyle] = useState(null);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const selectedItemRef = useRef(null);

  const selectedLabel = useMemo(() => getTimeZoneLabel(value), [value]);

  const filtered = useMemo(() => {
    const query = search.trim();
    if (query) {
      return filterTimeZones(TIME_ZONE_OPTIONS, query).slice(0, MAX_RESULTS);
    }
    return buildDefaultList(TIME_ZONE_OPTIONS, value);
  }, [search, value]);

  const updatePanelPosition = () => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
      zIndex: 2000,
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    updatePanelPosition();

    const onDoc = (event) => {
      const clickedInsideTrigger = wrapRef.current?.contains(event.target);
      const clickedInsidePanel = panelRef.current?.contains(event.target);
      if (!clickedInsideTrigger && !clickedInsidePanel) {
        setOpen(false);
        setSearch('');
      }
    };

    const onLayoutChange = () => updatePanelPosition();

    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onLayoutChange);
    window.addEventListener('scroll', onLayoutChange, true);
    const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 0);
    const scrollTimer = window.setTimeout(() => {
      selectedItemRef.current?.scrollIntoView({ block: 'nearest' });
    }, 0);

    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onLayoutChange);
      window.removeEventListener('scroll', onLayoutChange, true);
      window.clearTimeout(focusTimer);
      window.clearTimeout(scrollTimer);
    };
  }, [open, value]);

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
    if (open) setSearch('');
  };

  const handlePick = (zoneValue) => {
    onChange(zoneValue);
    setOpen(false);
    setSearch('');
  };

  const panel = open && panelStyle ? (
    <div
      ref={panelRef}
      className="[background:var(--bg-primary,_#fff)] tw-border-[1px] tw-border-solid tw-border-[var(--border-color,_#ced4da)] tw-rounded-[var(--radius-md,_0.375rem)] tw-shadow-[var(--shadow-lg,_0_0.5rem_1rem_rgba(0,_0,_0,_0.15))] tw-overflow-hidden tw-fixed timezone-dropdown-panel timezone-dropdown-panel--floating"
      style={panelStyle}
    >
      <div className={"tw-relative tw-p-[0.65rem] tw-border-b-[1px] tw-border-solid tw-border-b-[var(--border-color,_#ced4da)] [&_input]:tw-w-full [&_input]:tw-p-[0.5rem_0.75rem_0.5rem_2.25rem] [&_input]:tw-border-[1px] [&_input]:tw-border-solid [&_input]:tw-border-[var(--border-color,_#ced4da)] [&_input]:tw-rounded-[var(--radius-md,_0.375rem)] [&_input]:[background:var(--bg-secondary,_#f8f9fa)] [&_input]:tw-text-[var(--text-primary,_#212529)] [&_input]:tw-text-[0.875rem] [&_input:focus]:[outline:none] [&_input:focus]:tw-border-[var(--primary-color,_#3152e8)] [&_input:focus]:[background:var(--bg-primary,_#fff)] timezone-dropdown-search"}>
        <Icon icon="mdi:magnify" className="tw-absolute tw-left-[1.1rem] tw-top-[50%] [transform:translateY(-50%)] tw-text-[var(--text-secondary,_#6c757d)] tw-text-[1rem] tw-pointer-events-none timezone-dropdown-search-icon" />
        <input
          ref={searchRef}
          type="text"
          placeholder="Search by city, country, or GMT offset..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          autoComplete="off"
        />
      </div>

      <div
        className={"tw-overflow-y-auto tw-overflow-x-hidden [overscroll-behavior:contain] [scrollbar-width:thin] [scrollbar-color:rgba(128,_128,_128,_0.5)_transparent] [&::-webkit-scrollbar]:tw-w-[8px] [&::-webkit-scrollbar-track]:[background:rgba(255,_255,_255,_0.06)] [&::-webkit-scrollbar-thumb]:[background:rgba(128,_128,_128,_0.5)] [&::-webkit-scrollbar-thumb]:tw-rounded-[4px] [&::-webkit-scrollbar-thumb:hover]:[background:rgba(128,_128,_128,_0.7)] timezone-dropdown-list"}
        role="listbox"
        style={{ maxHeight: `${VISIBLE_ITEM_COUNT * VISIBLE_ITEM_HEIGHT_PX}px` }}
      >
        {!search.trim() && (
          <div className="tw-p-[0.75rem_0.85rem] tw-text-[0.8125rem] tw-text-[var(--text-secondary,_#6c757d)] timezone-dropdown-hint">
            Showing {TIME_ZONE_OPTIONS.length} time zones — scroll or search to find yours
          </div>
        )}

        {search.trim() && filtered.length === 0 && (
          <div className="tw-p-[0.75rem_0.85rem] tw-text-[0.8125rem] tw-text-[var(--text-secondary,_#6c757d)] tw-text-center timezone-dropdown-empty">No time zones found</div>
        )}

        {filtered.map((zone) => (
          <button
            key={zone.value}
            type="button"
            ref={zone.value === value ? selectedItemRef : null}
            className={`tw-block tw-w-full tw-min-h-[40px] tw-p-[0.6rem_0.85rem] tw-border-0 tw-bg-[transparent] tw-text-[var(--text-primary,_#212529)] tw-text-[0.875rem] tw-leading-[1.35] tw-text-left tw-cursor-pointer [&:hover]:[background:var(--bg-secondary,_#f8f9fa)] [&.is-selected]:[background:var(--bg-secondary,_#f8f9fa)] [&.is-selected]:tw-text-[var(--primary-color,_#3152e8)] [&.is-selected]:tw-font-semibold timezone-dropdown-item${zone.value === value ? "   is-selected" : ""}`}
            role="option"
            aria-selected={zone.value === value}
            onClick={() => handlePick(zone.value)}
          >
            {zone.label}
          </button>
        ))}

        {search.trim() && filtered.length === MAX_RESULTS && (
          <div className="tw-p-[0.75rem_0.85rem] tw-text-[0.8125rem] tw-text-[var(--text-secondary,_#6c757d)] timezone-dropdown-hint">
            Showing first {MAX_RESULTS} matches. Refine your search.
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="tw-relative tw-w-[50%] tw-max-w-full max-[768px]:tw-w-full timezone-dropdown-wrapper" ref={wrapRef}>
      <button
        type="button"
        className={`tw-flex tw-items-center tw-justify-between tw-w-full tw-min-h-[42px] tw-p-[0.5rem_0.75rem] tw-border-[1px] tw-border-solid tw-border-[var(--border-color,_#ced4da)] tw-rounded-[var(--radius-md,_0.375rem)] [background:var(--bg-secondary,_#fff)] tw-text-[var(--text-primary,_#212529)] tw-text-[0.9375rem] tw-leading-[1.4] tw-text-left tw-cursor-pointer [transition:border-color_0.2s_ease,_box-shadow_0.2s_ease] [&:hover:not(.is-disabled)]:tw-border-[var(--primary-color,_#3152e8)] [&:focus-visible]:[outline:none] [&:focus-visible]:tw-border-[var(--primary-color,_#3152e8)] [&:focus-visible]:tw-shadow-[0_0_0_3px_rgba(49,_82,_232,_0.15)] [&.is-disabled]:tw-opacity-[0.65] [&.is-disabled]:tw-cursor-not-allowed timezone-dropdown-trigger${disabled ? "   is-disabled" : ""}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="[flex:1] tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-pr-2 timezone-dropdown-value" title={selectedLabel || placeholder}>
          {selectedLabel || placeholder}
        </span>
        <Icon icon="mdi:chevron-down" className={`tw-shrink-[0] tw-text-[var(--text-secondary,_#667085)] tw-text-[1.125rem] [transition:transform_0.2s_ease] [&.is-open]:[transform:rotate(180deg)] timezone-dropdown-icon${open ? "   is-open" : ""}`} />
      </button>

      {panel && createPortal(panel, document.body)}
    </div>
  );
}
